import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Paper,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
} from "@mui/material";
import { Add, Edit, Delete, Email, Phone } from "@mui/icons-material";
import {
  getContactosProveedor,
  addContactoProveedor,
  updateContactoProveedor,
  deleteContactoProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";

import ChatBubbleIcon from "@mui/icons-material/ChatBubble";

import ProveedoresContactsDrawer from "./ProveedoresContactsDrawer";

const HEADER_BG = "#90b2b7";

const ProveedoresContacts = ({ proveedor }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [contactos, setContactos] = useState([]);
  const [nuevoContacto, setNuevoContacto] = useState({
    Nombre: "",
    Puesto: "",
    Email: "",
    Telefono: "",
    Comentario: "",
  });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedContacto, setSelectedContacto] = useState(null);

  useEffect(() => {
    if (proveedor?.CODPROV) fetchContactos();
  }, [proveedor]);

  const fetchContactos = async () => {
    try {
      const data = await getContactosProveedor(proveedor.CODPROV);
      setContactos(data);
    } catch (err) {
      console.error("❌ Error al obtener contactos:", err);
    }
  };

  const handleAdd = async () => {
    if (!nuevoContacto.Nombre) return;
    try {
      await addContactoProveedor(proveedor.CODPROV, nuevoContacto);
      setNuevoContacto({
        Nombre: "",
        Puesto: "",
        Email: "",
        Telefono: "",
        Comentario: "",
      });
      fetchContactos();
    } catch (error) {
      console.error("❌ Error al crear contacto:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteContactoProveedor(confirmDelete);
      setConfirmDelete(null);
      fetchContactos();
    } catch (error) {
      console.error("❌ Error al eliminar contacto:", error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        maxWidth: 440,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: HEADER_BG,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          zIndex: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 700 }}>
          {t("ProveedoresPage.contactsTitle")}
        </Typography>

        <Tooltip title={t("ProveedoresPage.addNewContact")}>
          <IconButton
            size="small"
            aria-label={t("ProveedoresPage.addNewContact")}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.6)",
              "&:hover": { backgroundColor: "#ffac7e" },
            }}
            onClick={() => {
              setSelectedContacto(null);
              setDrawerOpen(true);
            }}
          >
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {contactos.length === 0 ? (
        <Typography
          color="text.secondary"
          sx={{
            mt: 1.5,
            px: 0.5,
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          {t("ProveedoresPage.noContactsYet")}
        </Typography>
      ) : (
        <List
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            mt: 1,
            py: 0,
            "& .MuiListItem-root": { paddingLeft: "4px", px: 0 },
          }}
        >
          {contactos.map((c) => (
            <React.Fragment key={c.id_Contacto}>
              <ListItem sx={{ pt: 0, pr: 0 }}>
                <Paper
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "6px",
                    border: `1px solid ${theme.palette.divider}`,
                    width: "100%",
                    padding: "1em",
                    mt: 0.2,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#90b2b7" }}>
                    {c.Nombre}
                  </Typography>
                  <Typography variant="body2">{c.Puesto}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Email sx={{ color: "#90b2b7", mr: 1 }} />
                    <Typography variant="body2">
                      {c.Email || "No email"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Phone sx={{ color: "#34afc7", mr: 1 }} />
                    <Typography variant="body2">
                      {c.Telefono || "No phone"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Tooltip title="WeChat">
                      <ChatBubbleIcon sx={{ color: "#ffac7e", mr: 1 }} />{" "}
                      {/* 💬 icono verde */}
                    </Tooltip>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mr: 0.5 }}
                    >
                      {c.Wechat ? c.Wechat : "—"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    {c.Comentario || "—"}
                  </Typography>

                  {/* 🗑️ botones */}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                  >
                    <Tooltip title="Edit Contact">
                      <IconButton
                        sx={{
                          color: "#90b2b7",
                          border: "1px solid #90b2b7",
                          "&:hover": {
                            backgroundColor: "#90b2b7",
                            color: "#fff",
                          },
                          mr: 1,
                        }}
                        onClick={() => {
                          setSelectedContacto(c);
                          setDrawerOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Contact">
                      <IconButton
                        sx={{
                          color: "#90b2b7",
                          border: "1px solid #90b2b7",
                          "&:hover": { backgroundColor: "red", color: "#fff" },
                        }}
                        onClick={() => setConfirmDelete(c.id_Contacto)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* 🔥 Dialog Confirm Delete */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle>{t("ProveedoresPage.deleteContactTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("ProveedoresPage.deleteContactBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>
            {t("ProveedoresPage.cancel")}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            {t("ProveedoresPage.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <ProveedoresContactsDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        proveedor={proveedor}
        contacto={selectedContacto}
        onSave={fetchContactos}
      />
    </Box>
  );
};

export default ProveedoresContacts;
